import { useMutation, useQueryClient } from '@tanstack/react-query';

import { resumeTimer } from '../api/active-timer.api';
import { ACTIVE_TIMER_QUERY_KEYS } from '../constants/active-timer-query-keys';
import { ActiveTimerResponse, ResumeTimerRequest } from '../types/dto/active-timer.dto';
import { notifyActiveTimerChanged } from './use-active-timer';

export const useResumeTimer = () => {
  const queryClient = useQueryClient();

  return useMutation<ActiveTimerResponse, Error, ResumeTimerRequest>({
    mutationFn: values => resumeTimer(values),
    // The POST returns the full authoritative timer — seed the cache directly,
    // no redundant follow-up GET (OPS-1538 load review). Cross-tab sync below.
    // Discard any in-flight GET /worklogs/timer BEFORE mutating. Without this,
    // a stale fetch started earlier (e.g. by the return-to-tab revalidation)
    // can resolve AFTER onSuccess seeds the fresh state and overwrite it with
    // pre-mutation data - the root cause of the "timer flashes and disappears"
    // / diverged timer states after a long-idle tab (OPS-1538 / OPS-1625).
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ACTIVE_TIMER_QUERY_KEYS.all });
    },
    onSuccess: data => {
      queryClient.setQueryData(ACTIVE_TIMER_QUERY_KEYS.all, {
        timer: data,
        fetchedAt: Date.now(),
      });
    },
    onSettled: () => {
      notifyActiveTimerChanged();
    },
  });
};
